import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Radio, LinearProgress, IconButton, TextField, Chip } from '@mui/material';
import { Delete as DeleteIcon, Add as AddIcon, ExpandMore as ExpandIcon, ExpandLess as CollapseIcon } from '@mui/icons-material';
import { blogService } from '../services/blogService';
import { useAuthStore } from '../store/authStore';
import { useNavigate } from 'react-router-dom';

interface PollOption { id: number; text: string; votes: number; }
interface Poll { id: number; question: string; options: PollOption[]; total_votes: number; user_vote: number | null; }

export const PollDisplay: React.FC<{ poll: Poll; onVoted?: () => void }> = ({ poll, onVoted }) => {
  const { isAuthenticated, user } = useAuthStore();
  const navigate = useNavigate();
  const [voting, setVoting] = useState(false);
  const [selected, setSelected] = useState<number | null>(poll.user_vote ?? null);
  const [answering, setAnswering] = useState(poll.user_vote == null);
  const [showVoters, setShowVoters] = useState(false);
  const [votersData, setVotersData] = useState<any[] | null>(null);
  const [loadingVoters, setLoadingVoters] = useState(false);

  const isAdmin = user?.role === 'site_admin';
  const hasVoted = poll.user_vote != null;

  useEffect(() => {
    setSelected(poll.user_vote ?? null);
    if (poll.user_vote != null) setAnswering(false);
  }, [poll.id, poll.user_vote]);

  const handleSubmit = async () => {
    if (!isAuthenticated) { navigate('/login'); return; }
    if (selected == null || voting) return;
    try {
      setVoting(true);
      await blogService.votePoll(poll.id, selected);
      setAnswering(false);
      onVoted?.();
    } catch {} finally { setVoting(false); }
  };

  const handleToggleVoters = async () => {
    if (showVoters) { setShowVoters(false); return; }
    setShowVoters(true);
    if (!votersData) {
      try {
        setLoadingVoters(true);
        const data = await blogService.getPollVotes(poll.id);
        setVotersData(data.options);
      } catch {} finally { setLoadingVoters(false); }
    }
  };

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2 }, bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Typography variant="subtitle2" fontWeight={700}>📊 {poll.question}</Typography>
        {isAdmin && (
          <Button size="small" onClick={handleToggleVoters} sx={{ fontSize: '0.7rem', minWidth: 0, px: 1 }}
            endIcon={showVoters ? <CollapseIcon sx={{ fontSize: 14 }} /> : <ExpandIcon sx={{ fontSize: 14 }} />}>
            Голоса
          </Button>
        )}
      </Box>

      {/* Режим ответа: выбор варианта + кнопка «Ответить» */}
      {answering && (
        <>
          {poll.options.map(opt => (
            <Box key={opt.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Radio checked={selected === opt.id} onChange={() => setSelected(opt.id)} disabled={voting} size="small" sx={{ p: 0 }} />
              <Typography variant="body2" sx={{ flex: 1, fontSize: '0.85rem', cursor: 'pointer' }}
                onClick={() => !voting && setSelected(opt.id)}>
                {opt.text}
              </Typography>
            </Box>
          ))}
          <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
            <Button variant="contained" size="small" onClick={handleSubmit}
              disabled={voting || selected == null}
              sx={{ bgcolor: '#1565c0', '&:hover': { bgcolor: '#0d47a1' } }}>
              {voting ? 'Отправка...' : 'Ответить'}
            </Button>
            {hasVoted && (
              <Button variant="text" size="small" onClick={() => { setSelected(poll.user_vote); setAnswering(false); }} sx={{ fontSize: '0.75rem' }}>
                Отмена
              </Button>
            )}
          </Box>
        </>
      )}

      {/* Режим результатов: пересчитанные данные с сервера */}
      {!answering && (
        <>
          {poll.options.map(opt => {
            const pct = poll.total_votes > 0 ? Math.round((opt.votes / poll.total_votes) * 100) : 0;
            const isSelected = poll.user_vote === opt.id;
            const voterOption = votersData?.find(v => v.option_id === opt.id);
            return (
              <Box key={opt.id} sx={{ mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
                  <Typography variant="body2" sx={{ flex: 1, fontSize: '0.85rem', fontWeight: isSelected ? 700 : 400 }}>
                    {isSelected && '✓ '}{opt.text}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">{opt.votes}</Typography>
                  <Typography variant="caption" fontWeight={700} color={isSelected ? 'primary' : 'text.secondary'}>{pct}%</Typography>
                </Box>
                <LinearProgress variant="determinate" value={pct}
                  sx={{ height: 4, borderRadius: 2, bgcolor: '#e2e8f0', '& .MuiLinearProgress-bar': { bgcolor: isSelected ? '#1565c0' : '#94a3b8' } }} />
                {/* Voter list (admin only) */}
                {isAdmin && showVoters && voterOption && voterOption.voters.length > 0 && (
                  <Box sx={{ ml: 4, mt: 0.5, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {voterOption.voters.map((v: any, vi: number) => (
                      <Chip key={vi} label={v.name} size="small" sx={{ height: 20, fontSize: '0.65rem' }} />
                    ))}
                  </Box>
                )}
              </Box>
            );
          })}

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mt: 1, flexWrap: 'wrap' }}>
            <Typography variant="caption" color="text.secondary">
              {poll.total_votes} голос{poll.total_votes % 10 === 1 && poll.total_votes % 100 !== 11 ? '' : poll.total_votes % 10 >= 2 && poll.total_votes % 10 <= 4 && (poll.total_votes % 100 < 10 || poll.total_votes % 100 >= 20) ? 'а' : 'ов'}
              {loadingVoters && ' · Загрузка...'}
            </Typography>
            {hasVoted && isAuthenticated && (
              <Button variant="text" size="small" onClick={() => { setSelected(poll.user_vote); setAnswering(true); }} sx={{ fontSize: '0.72rem' }}>
                Изменить ответ
              </Button>
            )}
          </Box>
        </>
      )}
    </Box>
  );
};

export const PollCreator: React.FC<{ articleId?: number; onCreated?: (pollId: number) => void }> = ({ articleId, onCreated }) => {
  const [question, setQuestion] = useState('');
  const [options, setOptions] = useState(['', '']);
  const [saving, setSaving] = useState(false);

  const handleCreate = async () => {
    if (!articleId || !question.trim()) return;
    const validOptions = options.filter(o => o.trim());
    if (validOptions.length < 2) return;
    try {
      setSaving(true);
      const result = await blogService.createPoll(articleId, question.trim(), validOptions);
      setQuestion('');
      setOptions(['', '']);
      onCreated?.(result.poll_id);
    } catch {} finally { setSaving(false); }
  };

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2 }, bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0' }}>
      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>📊 Опрос</Typography>
      <TextField fullWidth size="small" label="Вопрос" value={question}
        onChange={e => setQuestion(e.target.value)} sx={{ mb: 1.5 }} />
      {options.map((opt, i) => (
        <Box key={i} sx={{ display: 'flex', gap: 0.5, mb: 0.75 }}>
          <TextField fullWidth size="small" label={`Вариант ${i + 1}`} value={opt}
            onChange={e => { const n = [...options]; n[i] = e.target.value; setOptions(n); }} />
          {options.length > 2 && (
            <IconButton size="small" onClick={() => setOptions(options.filter((_, j) => j !== i))}>
              <DeleteIcon sx={{ fontSize: 16 }} />
            </IconButton>
          )}
        </Box>
      ))}
      {options.length < 10 && (
        <Button size="small" startIcon={<AddIcon />} onClick={() => setOptions([...options, ''])}
          sx={{ fontSize: '0.75rem', mb: 1.5 }}>Добавить вариант</Button>
      )}
      <Button variant="contained" size="small" onClick={handleCreate}
        disabled={saving || !question.trim() || options.filter(o => o.trim()).length < 2}
        sx={{ bgcolor: '#1565c0', '&:hover': { bgcolor: '#0d47a1' } }}>
        {saving ? 'Сохранение...' : 'Добавить опрос'}
      </Button>
    </Box>
  );
};